import React from "react";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Clock, XCircle, Stethoscope, LogOut, RefreshCw } from "lucide-react";

export default function ClinicianPending() {
  const { user, logout, refreshUser } = useAuth();
  const { language } = useLanguage();
  const navigate = useNavigate();
  const bg = language === "bg";

  const handleLogout = async () => {
    navigate("/", { replace: true });
    await logout();
  };

  const status = user?.clinician_status || "pending";
  const isPending = status === "pending";
  const isRejected = status === "rejected";

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F5F3FF] via-[#FAFBFC] to-[#F0F9FF] flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo (wordmark вграден в PNG) */}
        <div className="text-center mb-6">
          <img src="/nutramilo-logo.png" alt="Nutramilo" className="mx-auto h-14 w-auto object-contain" />
        </div>

        <Card className="border-0 shadow-xl" data-testid="clinician-pending-card">
          <CardContent className="p-8 text-center space-y-5">
            {isPending ? (
              <>
                <div className="w-20 h-20 mx-auto rounded-full bg-amber-50 flex items-center justify-center">
                  <Clock className="w-10 h-10 text-amber-500" />
                </div>
                <h2 className="text-lg font-bold text-[#1A1A2E]">
                  {bg ? "Заявката ви се преглежда" : "Your Application is Under Review"}
                </h2>
                <p className="text-sm text-[#64748B] leading-relaxed">
                  {bg
                    ? "Благодарим ви за регистрацията! Нашият екип преглежда вашите документи и професионална квалификация. Ще получите достъп веднага след одобрение."
                    : "Thank you for registering! Our team is reviewing your documents and professional qualifications. You will receive access as soon as your application is approved."}
                </p>
                <div className="flex items-center justify-center gap-2 text-xs text-amber-600 bg-amber-50 rounded-lg p-3">
                  <Stethoscope className="w-4 h-4" />
                  {bg ? "Обикновено отнема до 24 часа" : "Usually takes up to 24 hours"}
                </div>
              </>
            ) : isRejected ? (
              <>
                <div className="w-20 h-20 mx-auto rounded-full bg-red-50 flex items-center justify-center">
                  <XCircle className="w-10 h-10 text-red-500" />
                </div>
                <h2 className="text-lg font-bold text-[#1A1A2E]">
                  {bg ? "Заявката е отхвърлена" : "Application Rejected"}
                </h2>
                <p className="text-sm text-[#64748B] leading-relaxed">
                  {bg
                    ? "За съжаление, вашата заявка не беше одобрена. Моля, свържете се с нас за повече информация."
                    : "Unfortunately, your application was not approved. Please contact us for more information."}
                </p>
              </>
            ) : null}

            <div className="flex gap-3 justify-center pt-2">
              <Button variant="outline" onClick={refreshUser} data-testid="clinician-refresh-status"
                className="rounded-xl text-sm">
                <RefreshCw className="w-4 h-4 mr-1.5" />
                {bg ? "Провери статус" : "Check Status"}
              </Button>
              <Button variant="ghost" onClick={handleLogout} data-testid="clinician-logout"
                className="rounded-xl text-sm text-gray-500">
                <LogOut className="w-4 h-4 mr-1.5" />
                {bg ? "Изход" : "Logout"}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
